import { Pill, type Tone } from "~/components/status-badge";
import type { StorefrontResponse } from "~/lib/queries";

/**
 * Whether this storefront would actually receive a callback.
 *
 * Mirrors Storefront.HasCallbackConfigured() in internal/models/storefront.go:
 * a destination, a secret to sign with, AND the kill switch left on. Reporting
 * "configured" for a storefront whose callbacks are switched off would answer
 * the wrong question -- the one the list exists to answer is "is this merchant
 * actually set up?", and a disabled callback is not.
 */
export type CallbackState = "configured" | "disabled" | "none";

export function callbackState(storefront: StorefrontResponse): CallbackState {
  const addressed = Boolean(storefront.callback_url) && storefront.has_callback_secret;
  if (!addressed) return "none";
  return storefront.callback_enabled ? "configured" : "disabled";
}

const LABELS: Record<CallbackState, { tone: Tone; text: string }> = {
  configured: { tone: "success", text: "configured" },
  // Someone turned it off deliberately. Not broken, but worth noticing.
  disabled: { tone: "warning", text: "disabled" },
  none: { tone: "neutral", text: "not configured" },
};

export function CallbackBadge({ storefront }: { storefront: StorefrontResponse }) {
  const { tone, text } = LABELS[callbackState(storefront)];
  return <Pill tone={tone}>{text}</Pill>;
}

/** "oauth" / "static" / none, as a word rather than a boolean. */
export function credentialLabel(storefront: StorefrontResponse): string {
  return storefront.credential_type || "none";
}
